import React from "react";

/**
 * Loan Obligations Month-wise View.
 * Shows EMI debits out vs loan credits in per month.
 * Detects loan-to-repay pattern (debt spiral indicator).
 * Data source: Finbox/OneMoney — No AI credits consumed.
 */
export default function LoanObligationsView({ incomeProfileData, selectedIndex }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const displayData = incomeProfileData?.[selectedIndex] || {};
  const lenderDebits = displayData?.lenderDebit || [];
  const lenderCredits = displayData?.lenderCredit || [];

  // Group debits and credits by month
  const monthlyData = buildMonthlyData(lenderDebits, lenderCredits);

  if (monthlyData.length === 0) {
    return null;
  }

  // Calculate totals
  const totalEmiOut = monthlyData.reduce((s, m) => s + m.totalDebit, 0);
  const totalLoansIn = monthlyData.reduce((s, m) => s + m.totalCredit, 0);
  const totalDebitCount = monthlyData.reduce((s, m) => s + m.debitCount, 0);
  const totalCreditCount = monthlyData.reduce((s, m) => s + m.creditCount, 0);
  const emiFundedByLoans = totalEmiOut > 0 ? Math.round((totalLoansIn / totalEmiOut) * 100) : 0;
  const hasLoanToRepay = emiFundedByLoans > 50;

  const formatCurrency = (amt) => "₹" + Math.round(amt).toLocaleString("en-IN");

  return (
    <div style={{ background: "#fff", borderRadius: "10px", border: "1px solid #e0e0e0", marginBottom: "16px", marginLeft: "1em", marginRight: "2em", overflow: "hidden" }}>
      {/* Header - always visible, clickable */}
      <div onClick={() => setIsOpen(!isOpen)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", cursor: "pointer", background: isOpen ? "#fff" : "#f8f9fa" }}>
        <h3 style={{ fontSize: "14px", fontWeight: 600, margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
          🏦 Loan Obligations — Month-wise View
          {!isOpen && <span style={{ fontSize: "11px", fontWeight: 400, color: "#667085", marginLeft: "12px" }}>EMI Out: {formatCurrency(totalEmiOut)} • Loans In: {formatCurrency(totalLoansIn)}{hasLoanToRepay ? " • ⚠️ Debt Spiral" : ""}</span>}
        </h3>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontSize: "10px", color: "#667085" }}>Source: Finbox / OneMoney • No AI credits</span>
          <span style={{ fontSize: "16px", color: "#667085", transition: "transform 0.3s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>▾</span>
        </div>
      </div>

      {/* Body - collapsible */}
      {isOpen && (
        <div style={{ padding: "0 20px 20px 20px" }}>

      {/* Summary Row */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
        <div style={{ background: "#eff6ff", borderRadius: "8px", padding: "10px 14px", flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#1e40af" }}>{totalDebitCount}</div>
          <div style={{ fontSize: "10px", color: "#667085" }}>EMI Debits (Out)</div>
        </div>
        <div style={{ background: "#eff6ff", borderRadius: "8px", padding: "10px 14px", flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#1e40af" }}>{formatCurrency(totalEmiOut)}</div>
          <div style={{ fontSize: "10px", color: "#667085" }}>Total EMI Paid Out</div>
        </div>
        <div style={{ background: "#ecfdf5", borderRadius: "8px", padding: "10px 14px", flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#166534" }}>{totalCreditCount}</div>
          <div style={{ fontSize: "10px", color: "#667085" }}>Loan Credits (In)</div>
        </div>
        <div style={{ background: "#ecfdf5", borderRadius: "8px", padding: "10px 14px", flex: 1, textAlign: "center" }}>
          <div style={{ fontSize: "18px", fontWeight: 700, color: "#166534" }}>{formatCurrency(totalLoansIn)}</div>
          <div style={{ fontSize: "10px", color: "#667085" }}>Total Loans Received</div>
        </div>
        <div style={{ background: hasLoanToRepay ? "#fef2f2" : "#f8f9fa", borderRadius: "8px", padding: "10px 14px", flex: 1, textAlign: "center", border: hasLoanToRepay ? "1px solid #fecaca" : "1px solid #e0e0e0" }}>
          <div style={{ fontSize: "16px", fontWeight: 700, color: hasLoanToRepay ? "#d32028" : "#333" }}>{hasLoanToRepay ? "⚠️ " : ""}{emiFundedByLoans}%</div>
          <div style={{ fontSize: "10px", color: hasLoanToRepay ? "#991b1b" : "#667085", fontWeight: hasLoanToRepay ? 600 : 400 }}>EMI Funded by Loans</div>
        </div>
      </div>

      {/* Month-wise Table */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden" }}>
        <thead>
          <tr style={{ background: "#f8f9fa" }}>
            <th style={{ padding: "8px", textAlign: "left", color: "#667085" }}>Month</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#1e40af" }}>EMI Out (Count)</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#1e40af" }}>Total EMI Out</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#166534" }}>Loans In (Count)</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#166534" }}>Total Loans In</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#667085" }}>Net</th>
            <th style={{ padding: "8px", textAlign: "center", color: "#667085" }}>Flag</th>
          </tr>
        </thead>
        <tbody>
          {monthlyData.map((m, i) => {
            const net = m.totalCredit - m.totalDebit;
            const isSpike = m.totalDebit > totalEmiOut / monthlyData.length * 2;
            const flag = isSpike && m.totalCredit > 0 ? "LOAN-TO-REPAY" : (m.totalCredit > 0 && m.totalCredit > m.totalDebit * 0.5 ? "WATCH" : null);

            return (
              <tr key={i} style={{ background: flag === "LOAN-TO-REPAY" ? "#fef2f2" : "transparent" }}>
                <td style={{ padding: "8px", borderBottom: "1px solid #f3f4f6", fontWeight: flag === "LOAN-TO-REPAY" ? 600 : 400, color: flag === "LOAN-TO-REPAY" ? "#d32028" : "#333" }}>
                  {m.month} {flag === "LOAN-TO-REPAY" ? "⚠️" : ""}
                </td>
                <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #f3f4f6" }}>{m.debitCount}</td>
                <td style={{ padding: "8px", textAlign: "center", color: "#1e40af", borderBottom: "1px solid #f3f4f6", fontWeight: flag === "LOAN-TO-REPAY" ? 600 : 400 }}>{formatCurrency(m.totalDebit)}</td>
                <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #f3f4f6" }}>{m.creditCount}</td>
                <td style={{ padding: "8px", textAlign: "center", color: "#166534", borderBottom: "1px solid #f3f4f6" }}>{formatCurrency(m.totalCredit)}</td>
                <td style={{ padding: "8px", textAlign: "center", color: net >= 0 ? "#166534" : "#d32028", borderBottom: "1px solid #f3f4f6" }}>{net >= 0 ? "+" : ""}{formatCurrency(net)}</td>
                <td style={{ padding: "8px", textAlign: "center", borderBottom: "1px solid #f3f4f6" }}>
                  {flag === "LOAN-TO-REPAY" && <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: 700 }}>LOAN-TO-REPAY</span>}
                  {flag === "WATCH" && <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 6px", borderRadius: "4px", fontSize: "9px", fontWeight: 600 }}>WATCH</span>}
                  {!flag && "—"}
                </td>
              </tr>
            );
          })}
          {/* Total Row */}
          <tr style={{ background: "#f8f9fa", fontWeight: 600 }}>
            <td style={{ padding: "8px" }}>TOTAL</td>
            <td style={{ padding: "8px", textAlign: "center" }}>{totalDebitCount}</td>
            <td style={{ padding: "8px", textAlign: "center", color: "#1e40af" }}>{formatCurrency(totalEmiOut)}</td>
            <td style={{ padding: "8px", textAlign: "center" }}>{totalCreditCount}</td>
            <td style={{ padding: "8px", textAlign: "center", color: "#166534" }}>{formatCurrency(totalLoansIn)}</td>
            <td style={{ padding: "8px", textAlign: "center", color: totalLoansIn - totalEmiOut >= 0 ? "#166534" : "#d32028" }}>{formatCurrency(totalLoansIn - totalEmiOut)}</td>
            <td style={{ padding: "8px" }}></td>
          </tr>
        </tbody>
      </table>

      {/* Alert */}
      {hasLoanToRepay && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "8px", padding: "12px 14px", marginTop: "14px" }}>
          <div style={{ fontSize: "12px", fontWeight: 600, color: "#991b1b", marginBottom: "4px" }}>🚨 Loan-to-Repay Pattern Detected</div>
          <div style={{ fontSize: "11px", color: "#7f1d1d", lineHeight: 1.6 }}>
            Borrower received {formatCurrency(totalLoansIn)} in new loans over {monthlyData.length} months while paying {formatCurrency(totalEmiOut)} in EMIs. <strong>{emiFundedByLoans}% of EMI payments are funded by new borrowings.</strong> This is a debt spiral indicator — verify income sustainability.
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
}

/**
 * Builds monthly aggregated data from lender debit and credit transactions.
 */
function buildMonthlyData(lenderDebits, lenderCredits) {
  const monthMap = new Map();

  // Process debits (filter out "Total" summary rows)
  const actualDebits = lenderDebits.filter(t => {
    if (!t) return false;
    if (t.transaction_note === "Total") return false;
    if (t.transaction_note === "Total Amount") return false;
    if (!t.date) return false;
    if (!t.amount || Number(t.amount) <= 0) return false;
    return true;
  });
  for (const txn of actualDebits) {
    const monthKey = getMonthKey(txn.date);
    if (!monthKey) continue;
    if (!monthMap.has(monthKey)) monthMap.set(monthKey, { month: monthKey, debitCount: 0, totalDebit: 0, creditCount: 0, totalCredit: 0 });
    const entry = monthMap.get(monthKey);
    entry.debitCount++;
    entry.totalDebit += Number(txn.amount) || 0;
  }

  // Process credits
  const actualCredits = (lenderCredits || []).filter(t => {
    if (!t) return false;
    if (t.transaction_note === "Total") return false;
    if (t.transaction_note === "Total Amount") return false;
    if (t.transaction_note === "No Loans") return false;
    if (!t.date) return false;
    if (!t.amount || Number(t.amount) <= 0) return false;
    return true;
  });
  for (const txn of actualCredits) {
    const monthKey = getMonthKey(txn.date);
    if (!monthKey) continue;
    if (!monthMap.has(monthKey)) monthMap.set(monthKey, { month: monthKey, debitCount: 0, totalDebit: 0, creditCount: 0, totalCredit: 0 });
    const entry = monthMap.get(monthKey);
    entry.creditCount++;
    entry.totalCredit += Number(txn.amount) || 0;
  }

  // Sort by date
  return Array.from(monthMap.values()).sort((a, b) => a.month.localeCompare(b.month));
}

function getMonthKey(dateStr) {
  if (!dateStr) return null;
  try {
    // Handle DD/MM/YYYY format (common in Finbox data)
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        const monthIdx = parseInt(parts[1]) - 1;
        const year = parts[2];
        if (monthIdx >= 0 && monthIdx < 12) return `${months[monthIdx]} ${year}`;
      }
    }
    // Handle "Mon YYYY" format (e.g., "Feb 2026" — already a month key from Total rows)
    if (typeof dateStr === 'string' && /^[A-Z][a-z]{2}\s\d{4}$/.test(dateStr)) {
      return dateStr;
    }
    // Handle ISO date or other parseable formats
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return null;
  }
}
